import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {addStyle, Text, View} from "@khanacademy/wonder-blocks-core";
import {LabeledTextField} from "@khanacademy/wonder-blocks-form";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelLarge, LabelMedium} from "@khanacademy/wonder-blocks-typography";
import {doNotTranslate} from "@repo/i18n";
import {RequestInfo} from "@repo/shared-core";
import {mediaQueries} from "@repo/shared-styles";
import {staticUrl} from "@repo/urls";

import {RefreshForKhanmigo} from "#guide-browser-extension/refresh-for-khanmigo.tsx";

import strings from "../../strings.tsx";

import type {forgotPassword_forgotPassword_error as ForgotPasswordError} from "./__graphql-types__/forgotPassword.ts";

const StyledHr = addStyle("hr");
const StyledImg = addStyle("img");

type ForgotPasswordErrorCode = ForgotPasswordError["code"];

type Props = {
    // `email` isn't included in `phase` since it's common between
    // all possible variants of Phase it can take and including it
    // makes this more complicated without a clear benefit.
    email: string;
    phase: Phase;
    // Event handlers
    onResetPassword: () => unknown;
    onUpdateEmail: (newEmail: string) => unknown;
};

export type Phase =
    | {
          id: "unsubmitted";
      }
    | {
          id: "error";
          errorCode: ForgotPasswordErrorCode;
      }
    | {
          id: "success";
          devOnlyLink: string | null | undefined; // it's only defined in dev
      };

class ForgotPasswordView extends React.Component<Props> {
    renderResetForm(): React.ReactNode {
        const {email, onResetPassword, onUpdateEmail} = this.props;

        // TODO(FEI-3708): Wrap field in a <form/> to submit with keyboard
        return (
            <View style={styles.dialog}>
                <View style={styles.grid}>
                    <StyledImg
                        style={styles.icon}
                        src={staticUrl("/images/hand-tree.new.png")}
                        width="75"
                        height="75"
                        alt=""
                    />
                    <View style={styles.body}>
                        <LabeledTextField
                            testId="forgot-password-email"
                            label={t`Email`}
                            description={t`Enter your email to reset your password:`}
                            value={email}
                            onChange={(newValue) => onUpdateEmail(newValue)}
                        />
                        <Strut size={spacing.medium_16} />
                        <Button onClick={onResetPassword} disabled={!email}>
                            {t`Reset password`}
                        </Button>
                    </View>
                </View>
                <StyledHr style={styles.rule} />
                <Text>{strings.resetChildPassword()}</Text>
            </View>
        );
    }

    renderEmailSent(devOnlyLink?: string | null): React.ReactNode {
        const {email} = this.props;

        return (
            <View style={styles.dialog}>
                <View style={styles.grid}>
                    <StyledImg
                        style={styles.icon}
                        src={staticUrl("/images/hand-tree.new.png")}
                        width="75"
                        height="75"
                        alt=""
                    />
                    <View>
                        <LabelMedium>
                            {t`We've sent you a message at:`}
                        </LabelMedium>
                        <LabelLarge style={styles.successEmailAddress}>
                            {email}
                        </LabelLarge>
                        <LabelMedium>
                            {t`Follow the link in that message to reset your password.`}
                        </LabelMedium>
                        {RequestInfo.isExtension && (
                            <RefreshForKhanmigo href="/guide-extension?extension=1" />
                        )}
                        {devOnlyLink && (
                            <Link href={devOnlyLink} style={styles.devOnlyLink}>
                                {doNotTranslate`dev-only password reset link`}
                            </Link>
                        )}
                    </View>
                </View>
            </View>
        );
    }

    renderError(errorCode: ForgotPasswordErrorCode): React.ReactNode {
        return (
            <View style={styles.errorDialog}>
                <LabelMedium>
                    {errorCode === "TOO_MANY_REQUESTS"
                        ? t`Sorry, you've made too many requests.`
                        : t`Something went wrong. Please try again.`}
                </LabelMedium>
            </View>
        );
    }

    render(): React.ReactNode {
        const {phase} = this.props;

        return (
            <React.Fragment>
                {phase.id === "error" && this.renderError(phase.errorCode)}
                {(phase.id === "unsubmitted" || phase.id === "error") &&
                    this.renderResetForm()}
                {phase.id === "success" &&
                    this.renderEmailSent(phase.devOnlyLink)}
            </React.Fragment>
        );
    }
}

const CARD_WIDTH = 420;

const styles = StyleSheet.create({
    grid: {
        display: "grid",
        columnGap: spacing.large_24,
        [mediaQueries.mdOrLarger]: {
            gridTemplateAreas: `"icon body"`,
        },
        [mediaQueries.smOrSmaller]: {
            gridTemplateAreas: `"body body"`,
        },
    },
    icon: {
        gridArea: "icon",
        // Even though "icon" doesn't appear in the grid template for small
        // devices, we have to set it to `display: none;` in order for it to
        // not show up in the browser rendering.
        [mediaQueries.smOrSmaller]: {
            display: "none",
        },
    },
    body: {
        gridArea: "body",
    },
    errorDialog: {
        [mediaQueries.mdOrLarger]: {
            backgroundColor: color.fadedRed16,
            width: CARD_WIDTH,
            padding: spacing.large_24,
            borderRadius: 4,
            borderColor: color.offBlack16,
            borderWidth: 1,
            marginBottom: spacing.large_24,
        },
        [mediaQueries.smOrSmaller]: {
            padding: spacing.large_24,
        },
    },
    dialog: {
        [mediaQueries.mdOrLarger]: {
            backgroundColor: color.offBlack8,
            width: CARD_WIDTH,
            padding: spacing.large_24,
            borderRadius: 4,
            borderColor: color.offBlack16,
            borderWidth: 1,
        },
        [mediaQueries.smOrSmaller]: {
            padding: spacing.large_24,
        },
    },
    rule: {
        marginTop: spacing.medium_16,
        marginBottom: spacing.medium_16,
        boxShadow: "none",
    },
    successEmailAddress: {
        margin: "auto",
        marginTop: spacing.medium_16,
        marginBottom: spacing.medium_16,
    },
    devOnlyLink: {
        marginTop: spacing.large_24,
    },
});

export default ForgotPasswordView;
